import {
    fetchEventSource,
    EventSourceMessage,
    FetchEventSourceInit,
} from '@microsoft/fetch-event-source';
import {
    Observable,
    Subscriber,
} from 'rxjs';

import { logger } from '@app/logger';
import { MeticulousSseUtils } from '@src/app/utils/meticulous/meticulousUtils';

import { HttpStatus } from './HttpStatus';

const networkErrorMessage = 'network error';

export function createSseConnection<T>(
    url: string,
    headers: Record<string, string> = {},
    reconnectAfterMs = 1000,
): SseConnection<T> {
    const controller = new AbortController();
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const observable: Observable<T> = new Observable((subscriber: Subscriber<T>) => {
        const config: FetchEventSourceInit = {
            // Close SSE connection when tab is not visible
            openWhenHidden: false,
            headers: headers,
            onopen: (response) => {
                MeticulousSseUtils.recordMeticulousSseEvent(url, 'onopen', response);

                if (response.ok && response.status === 200) {
                    return Promise.resolve();
                } else if (response.status >= 400 && response.status <= 599) {
                    throw new HttpStatusError(response.status);
                } else {
                    throw new SseFatalError(`Unexpected response status: ${response.status}`);
                }
            },
            onmessage: (message: EventSourceMessage) => {
                MeticulousSseUtils.recordMeticulousSseEvent(url, 'onmessage', message);

                const data = JSON.parse(message.data) as T;
                subscriber.next(data);
            },
            onerror: (error) => {
                MeticulousSseUtils.recordMeticulousSseEvent(url, 'onerror', error);

                if (error instanceof SseFatalError) {
                    subscriber.error(error);
                } else if (error instanceof RetriableCloseError) {
                    return reconnectAfterMs;
                } else if (error instanceof HttpStatusError) {
                    switch (error.status) {
                        case HttpStatus.ClientError:
                            return subscriber.error(error);
                        case HttpStatus.Forbidden:
                            return subscriber.error(error);
                        case HttpStatus.NotFound:
                            return reconnectAfterMs;
                        case HttpStatus.ServerError:
                            return reconnectAfterMs;
                        default:
                            logger.error(error);
                    }
                // This happens during internal testing because of BitDefender.
                // It breaks off SSE connections for no good reason.
                } else if (error?.message === networkErrorMessage) {
                    return reconnectAfterMs;
                } else {
                    subscriber.error(error);
                }
            },
            onclose: () => {
                MeticulousSseUtils.recordMeticulousSseEvent(url, 'onclose', undefined);

                throw new RetriableCloseError();
            },
            signal: controller.signal,
        };

        // When running in Meticulous replay environments, we want to allow Meticulous
        // to replay the events as per recordings, instead of really fetching them
        if (window.Meticulous?.isRunningAsTest) {
            MeticulousSseUtils.addMeticulousSseEventListeners(url, config);
        } else {
            fetchEventSource(url, config);
        }
    });

    return {
        observable,
        unsubscribe: () => {
            controller.abort();
        },
    };
}

export type SseConnection<T> = {
    observable: Observable<T>;
    unsubscribe: () => void;
};

export class HttpStatusError extends Error {
    constructor(public status: number) {
        super();
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export class SseFatalError extends Error {
    constructor(message: string) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export class RetriableCloseError extends Error {
    constructor() {
        super();
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
